import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Textarea,
} from '@roger/ui-components';
import { bulkAppendTaskNotes, updateTaskNotes } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface NotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Task IDs (orders) to update */
  taskIds: string[];
  /** When editing a single task, pass the current notes to pre-fill */
  initialNotes?: string;
  /** 'single' overwrites via PUT, 'bulk' appends via POST */
  mode: 'single' | 'bulk';
  onSuccess: () => void;
}

const NotesDialog: React.FC<NotesDialogProps> = ({
  open,
  onOpenChange,
  taskIds,
  initialNotes,
  mode,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { error: showError } = useToast();
  const count = taskIds.length;

  useEffect(() => {
    if (open) {
      setNotes(mode === 'single' ? (initialNotes ?? '') : '');
    }
  }, [open, mode, initialNotes]);

  const handleSubmit = async () => {
    if (taskIds.length === 0) return;
    setIsSubmitting(true);
    try {
      if (mode === 'single') {
        await updateTaskNotes({
          path: { task_id: taskIds[0] },
          body: { notes: notes.trim() || null },
        });
      } else {
        if (!notes.trim()) return;
        const { data } = await bulkAppendTaskNotes({
          body: { task_ids: taskIds, notes: notes.trim() },
        });
        if (data?.failed && data.failed.length > 0) {
          showError(
            `Failed to add notes to ${data.failed.length} of ${count} orders`
          );
        }
      }
      setNotes('');
      onOpenChange(false);
      onSuccess();
    } catch {
      showError('Failed to save notes');
    } finally {
      setIsSubmitting(false);
    }
  };

  const title =
    mode === 'single' ? 'Edit Notes' : `Add Notes to ${count} Orders`;

  const buttonLabel = mode === 'single' ? 'Save' : 'Add Notes';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder='Enter notes...'
          rows={4}
        />
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (mode === 'bulk' && !notes.trim())}
          >
            {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {buttonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotesDialog;
